import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/components/base/Toast';
import { ApiError } from '@/api/client';
import type { User } from '@/types';
import { ZaloCompleteProfile, type ZaloSignupPending } from './ZaloCompleteProfile';
import { ZaloMark } from './ZaloMark';

export default function LoginPage() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberLogin, setRememberLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [zaloLoading, setZaloLoading] = useState(false);
  const [zaloPending, setZaloPending] = useState<ZaloSignupPending | null>(null);
  const { user, login, getZaloAuthUrl, zaloCallback, zaloRegister } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const zaloCode = searchParams.get('code');
  const zaloState = searchParams.get('state');

  useEffect(() => {
    const remembered = localStorage.getItem('agrida_remember_login');
    if (!remembered) return;
    setPhone(remembered);
    setRememberLogin(true);
  }, []);

  // Always the landing page, whatever the eKYC state: eKYC only gates borrowing
  // (hạn mức, vay vốn), not the rest of the app. The toast in afterAuth still
  // tells a pending/need-more/failed account where it stands.
  const getKycRedirect = (user: User): string => (user.role === 'Nông hộ' ? '/nong-ho/tong-quan' : '/htx/tong-quan');

  /** Shared by both sign-in paths (mật khẩu và Zalo): greet according to eKYC
   * state, then route to the right landing page. */
  const afterAuth = useCallback((user: User) => {
    if (user.kycStatus === 'ekyc_verified') {
      showToast(`Xin chào, ${user.displayName}`, 'success');
    } else if (user.kycStatus === 'ekyc_pending') {
      showToast('Hồ sơ eKYC của bạn đang được xét duyệt', 'warning');
    } else if (user.kycStatus === 'ekyc_need_more') {
      showToast('Bạn cần bổ sung thông tin eKYC', 'warning');
    } else if (user.kycStatus === 'ekyc_failed') {
      showToast('eKYC của bạn bị từ chối. Vui lòng thực hiện lại.', 'error');
    } else if (user.role === 'Nông hộ') {
      showToast('Vui lòng hoàn tất xác thực danh tính (eKYC)', 'warning');
    } else {
      showToast(`Xin chào, ${user.displayName}`, 'success');
    }
    navigate(getKycRedirect(user));
  }, [navigate]);

  const doLogin = async (p: string, pw: string) => {
    setLoading(true);
    try {
      const user = await login(p, pw);
      if (rememberLogin) localStorage.setItem('agrida_remember_login', p);
      else localStorage.removeItem('agrida_remember_login');
      afterAuth(user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        showToast(err.message || 'Số điện thoại hoặc mật khẩu không đúng', 'error');
      } else if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Không thể đăng nhập, vui lòng thử lại', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const startZaloLogin = async () => {
    setZaloLoading(true);
    try {
      window.location.href = await getZaloAuthUrl();
    } catch (err) {
      setZaloLoading(false);
      showToast(err instanceof Error ? err.message : 'Không mở được đăng nhập Zalo', 'error');
    }
  };

  // Zalo bounces back to this page with ?code=&state=. The state row is single-use
  // server-side, so this must fire exactly once — hence the ref guard (StrictMode
  // runs effects twice in dev) and the immediate query-string cleanup, which also
  // stops a page refresh from replaying a spent code.
  const zaloExchangeStarted = useRef(false);
  useEffect(() => {
    if (!zaloCode || !zaloState || zaloExchangeStarted.current) return;
    zaloExchangeStarted.current = true;
    setSearchParams({}, { replace: true });

    setZaloLoading(true);
    zaloCallback(zaloCode, zaloState)
      .then((result) => {
        if ('user' in result) {
          afterAuth(result.user);
          return;
        }
        // Zalo sends both handshakes back to this same page, because
        // ZALO_REDIRECT_URI is a single whitelisted URL. 'linked' means the caller
        // was already signed in and was attaching Zalo from Cài đặt → Bảo mật, so
        // there is no session to establish — just report it and send them back.
        if (result.status === 'linked') {
          showToast('Đã liên kết tài khoản Zalo', 'success');
          navigate(user?.role === 'Hợp tác xã' ? '/htx/cai-dat' : '/nong-ho/cai-dat', { replace: true });
          return;
        }
        setZaloPending({
          signupToken: result.signupToken,
          suggestedName: result.suggestedName,
          pictureUrl: result.pictureUrl,
        });
      })
      .catch((err) => {
        showToast(err instanceof Error ? err.message : 'Đăng nhập Zalo không thành công', 'error');
      })
      .finally(() => setZaloLoading(false));
  }, [zaloCode, zaloState, zaloCallback, setSearchParams, afterAuth, navigate, user?.role]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!phone.trim()) newErrors.phone = 'Vui lòng nhập số điện thoại';
    if (!password.trim()) newErrors.password = 'Vui lòng nhập mật khẩu';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('Vui lòng nhập số điện thoại và mật khẩu', 'warning');
      return;
    }
    setErrors({});
    doLogin(phone.trim(), password);
  };

  const isSubmitDisabled = loading || !phone.trim() || !password.trim();

  if (zaloPending) {
    return (
      <ZaloCompleteProfile
        pending={zaloPending}
        onSubmit={async (payload) => {
          const user = await zaloRegister(payload);
          showToast('Đăng ký bằng Zalo thành công!', 'success');
          afterAuth(user);
        }}
        onCancel={() => setZaloPending(null)}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white flex items-center justify-center p-4 max-w-[430px] mx-auto">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-agrida-100 flex items-center justify-center">
            <span className="w-10 h-10 flex items-center justify-center">
              <i className="ri-plant-line text-agrida-600 text-3xl" />
            </span>
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900">Agrida</h1>
          <p className="text-sm text-gray-500 mt-2">Nền tảng quản lý Nông hộ & Hợp tác xã</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Số điện thoại</label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value.replace(/[^\d+]/g, '')); setErrors((prev) => ({ ...prev, phone: '' })); }}
              className={`w-full px-4 py-3 rounded-xl border text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent focus:bg-white transition-all ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              placeholder="Nhập số điện thoại"
            />
            {errors.phone && <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line text-xs" />{errors.phone}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors((prev) => ({ ...prev, password: '' })); }}
                className={`w-full px-4 py-3 pr-12 rounded-xl border text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent focus:bg-white transition-all ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                placeholder="Nhập mật khẩu"
              />
              <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-gray-400" aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}>
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
            {errors.password && <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line text-xs" />{errors.password}</p>}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={rememberLogin} onChange={(event) => setRememberLogin(event.target.checked)} className="h-4 w-4 rounded border-gray-300 accent-agrida-600" />
            Ghi nhớ số điện thoại
          </label>
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="w-full bg-agrida-600 hover:bg-agrida-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap min-h-[48px] text-sm cursor-pointer"
          >
            {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs text-gray-500">Hoặc</span>
          </div>
        </div>

        <button
          type="button"
          onClick={startZaloLogin}
          disabled={zaloLoading}
          className="flex min-h-[48px] w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
        >
          <ZaloMark />
          {zaloLoading ? 'Đang kết nối Zalo...' : 'Đăng nhập bằng Zalo'}
        </button>

        <div className="mt-4 text-center">
          <Link
            to="/dang-ky"
            className="text-sm text-agrida-600 font-medium hover:text-agrida-700 transition-colors"
          >
            Chưa có tài khoản? Đăng ký ngay
          </Link>
        </div>
      </div>
    </div>
  );
}
